<template>
  <div
      class="evidence-category-card__item"
      :class="{
        'evidence-category-card__item--incorrect': markedIncorrect || committedIncorrect,
        'evidence-category-card__item--readonly':  canEditItems && item.readOnly,
        'evidence-category-card__item--review': readOnly,
      }"
  >
    <div class="evidence-category-card__item-copy">
      <div class="evidence-category-card__item-header">
        <div class="evidence-category-card__item-name">
          {{ item.nameToken ? $t(item.nameToken) : formatLabel(item.name) }}{{ eyeSuffix }}
          <span v-if="structuredUnitToken || structuredUnitLabel">
            ({{ structuredUnitToken ? $t(structuredUnitToken) : structuredUnitLabel }})
          </span>
        </div>

        <span
            v-if="drafted"
            class="evidence-category-card__badge evidence-category-card__badge--edited"
            :title="String($t('app.assessment_mode.text.input.original_value', { value: originalValue || '—' }))"
        >{{ $t('app.assessment_mode.text.input.badge_edited') }}</span>

        <span
            v-if="markedIncorrect"
            class="evidence-category-card__badge evidence-category-card__badge--incorrect"
        >{{ $t('app.assessment_mode.text.input.badge_incorrect') }}</span>
      </div>

      <ObservationEditor
          v-if="canEditItems && item.structured && !item.readOnly"
          :item-id="item.id"
          :label="item.nameToken ? String($t(item.nameToken)) : formatLabel(item.name)"
          :observation-type="item.observationType || 'text'"
          :current-value="displayValue"
          :response-options="item.responseOptions || []"
          :numeric-options="item.numericOptions || null"
          :input="item.input"
          :blood-pressure-options="item.bloodPressureOptions || null"
          :marked-incorrect="markedIncorrect"
          :drafted="drafted"
          :can-remove="canRemove"
          @value-change="$emit('value-change', $event)"
          @remove="$emit('remove', $event)"
          @validation-change="$emit('validation-change', $event)"
      />

      <template v-else-if="canEditItems && !item.readOnly && !item.structured && editing">
        <input
            class="evidence-category-card__item-input"
            :value="editingValue"
            @input="handleEditInput"
            @keydown.enter="$emit('save', item.id)"
            @keydown.escape="$emit('cancel')"
        />
        <div class="evidence-category-card__item-actions">
          <button type="button" class="evidence-category-card__save-btn" @click="$emit('save', item.id)">
            {{ $t('app.assessment_mode.text.input.save') }}
          </button>
          <button type="button" class="evidence-category-card__cancel-btn" @click="$emit('cancel')">
            {{ $t('app.assessment_mode.text.input.cancel') }}
          </button>
        </div>
      </template>

      <div
          v-else
          class="evidence-category-card__item-value"
          :class="{
            'evidence-category-card__item-value--unknown':   isUnknownValue,
            'evidence-category-card__item-value--incorrect': markedIncorrect || committedIncorrect,
            'evidence-category-card__item-value--positive':  item.structured && observationStatus === 'positive',
            'evidence-category-card__item-value--negative':  item.structured && observationStatus === 'negative',
            'evidence-category-card__item-value--structured-unknown': item.structured && observationStatus === 'unknown',
          }"
      >
        <span v-if="item.structured && observationStatus">
          <span class="observation-status-chip" :class="`observation-status-chip--${observationStatus}`">
            {{ $t(statusLabelToken) }}
          </span>
        </span>
        <template v-else-if="item.structured">
          <span v-if="isUnknownValue" class="evidence-category-card__unknown-badge">!</span>
          {{ isUnknownValue ? $t('app.assessment_mode.text.input.option.unknown') : structuredDisplayValue }}
        </template>
        <template v-else-if="!item.structured">
          <span v-if="isUnknownValue" class="evidence-category-card__unknown-badge">!</span>
          {{ isUnknownValue ? $t('app.assessment_mode.text.input.option.unknown') : formattedDisplayValue }}
        </template>

        <span
            v-if="drafted && !item.structured"
            class="evidence-category-card__original-hint"
        >
          {{ $t('app.assessment_mode.text.input.original_value', { value: originalValue || '—' }) }}
        </span>
      </div>
    </div>

    <button
        v-if="canEditItems && item.editable && !item.readOnly && !item.structured && !editing && !markedIncorrect"
        type="button"
        class="evidence-category-card__edit"
        :aria-label="$t('app.assessment_mode.text.input.edit')"
        @click="$emit('start-edit', item.id, String(displayValue === null || displayValue === undefined ? '' : displayValue))"
    >
      <v-icon class="evidence-category-card__edit-icon">{{ mdiPencil }}</v-icon>
    </button>

    <button
        v-if="canEditItems && drafted && !editing"
        type="button"
        class="evidence-category-card__revert"
        @click="$emit('revert', item.id)"
    >
      {{ $t('app.assessment_mode.text.input.revert') }}
    </button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, getCurrentInstance, PropType } from 'vue';
import { mdiPencil } from '@mdi/js';
import ObservationEditor from './ObservationEditor.vue';
import type { EvidenceItem, ObservationStatus } from '../../types/text-mode.types';
import { formatAgeInDays, formatLabel, parseNumericWithUnit } from "@/features/text-mode/utils/text-utils";
import { canRemoveEvidenceItem } from '../../utils/evidence-removal';
import { attributeSuffix } from '@/utils/attributeSuffix';

type EvidenceValue = string | number | boolean | null;

export default defineComponent({
  name: 'EvidenceCategoryItem',

  components: {
    ObservationEditor,
  },

  props: {
    item: {
      type: Object as PropType<EvidenceItem>,
      required: true,
    },
    canEditItems: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    editingValue: {
      type: String,
      default: '',
    },
    displayValue: {
      type: [String, Number, Boolean, null] as unknown as PropType<EvidenceValue>,
      default: null,
    },
    originalValue: {
      type: [String, Number, Boolean, null] as unknown as PropType<EvidenceValue>,
      default: null,
    },
    observationStatus: {
      type: String as PropType<ObservationStatus | null>,
      default: null,
    },
    statusLabelToken: {
      type: String,
      default: '',
    },
    drafted: {
      type: Boolean,
      default: false,
    },
    markedIncorrect: {
      type: Boolean,
      default: false,
    },
    committedIncorrect: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const vm = getCurrentInstance();

    const translate = (token: string): string => {
      const t = vm?.proxy.$t;
      return typeof t === 'function' ? String(t.call(vm.proxy, token)) : token;
    };

    const formatNumber = (value: number): string => {
      const n = vm?.proxy.$n;
      return typeof n === 'function' ? String(n.call(vm.proxy, value, 'decimal')) : String(value);
    };

    const structuredValueParts = computed(() => {
      if (!props.item.structured) return null;
      return parseNumericWithUnit(props.displayValue);
    });

    const structuredUnitLabel = computed(() => {
      return structuredValueParts.value?.unit || '';
    });

    const structuredUnitToken = computed(() => {
      const unit = props.item.numericOptions?.unit;
      return unit ? `units.${unit}.short` : '';
    });

    const structuredDisplayValue = computed(() => {
      const numericParts = structuredValueParts.value;
      if (numericParts) {
        return numericParts.numberValue;
      }

      if (props.item.observationType === 'option' && typeof props.displayValue === 'string') {
        const value = props.displayValue;
        if (props.item.responseOptions && props.item.responseOptions.length > 0) {
          const matchingOption = props.item.responseOptions.find(opt => opt.value === value);
          if (matchingOption?.token) {
            return translate(matchingOption.token);
          }
        }
        return formatLabel(value);
      }

      return props.displayValue;
    });

    const handleEditInput = (event: Event): void => {
      emit('edit-input', (event.target as HTMLInputElement).value);
    };

    const formattedDisplayValue = computed((): string | number | boolean | null => {
      if (props.item.valueFormatter === 'age') {
        return formatAgeInDays(props.displayValue, translate, formatNumber);
      }

      if (props.item.valueToken && props.displayValue === props.item.value) {
        return translate(props.item.valueToken);
      }

      return props.displayValue;
    });

    const isUnknownValue = computed((): boolean =>
      formattedDisplayValue.value === null ||
      formattedDisplayValue.value === undefined ||
      formattedDisplayValue.value === '');

    const canRemove = computed((): boolean => canRemoveEvidenceItem(props.item));

    const eyeSuffix = computed((): string => (props.observationStatus === 'positive'
      ? attributeSuffix(props.item.attributes, translate)
      : ''));

    return {
      mdiPencil,
      isUnknownValue,
      formattedDisplayValue,
      structuredUnitLabel,
      structuredUnitToken,
      structuredDisplayValue,
      canRemove,
      eyeSuffix,
      formatLabel,
      handleEditInput,
    };
  },
});
</script>
